import { FC, useEffect, useState } from "react";
import { Alert, Button, Col, Descriptions, Input, Row, Tag, message } from "antd";
import { ImportOutlined } from "@ant-design/icons";
import Papa from "papaparse";

import { DealAnalysisOutput, Evaluation } from "../../../../types/ProjectTypes";
import { parseEnum } from "../../../../common/Enum";
import { convertGoogleSheet } from "../../../../common/api/ReportClient";
import { handleError } from "../../../../common/ErrorHandling";
import { GoogleShareHint } from "./GoogleShareHint";

// --- Derived types ---

type Summary = NonNullable<DealAnalysisOutput["summary"]>;
type DealEvaluation = Summary["evaluation"];
type Implication = Summary["implications"][number];
type NextAction = Summary["nextActions"][number];

// --- CSV parsing ---

const IMPLICATION_CATEGORIES = ["Commercial", "Legal", "Operational"] as const;

interface ParsedSummaryResult {
    summary: Partial<Summary>;
    errors: string[];
}

function parseNumber(val: string): number {
    const n = Number(val.replace(/[^0-9.-]/g, ""));
    if (isNaN(n)) throw new Error(`Invalid number: "${val}"`);
    return n;
}

function parseEvaluation(val: string): Evaluation | undefined {
    return parseEnum(Evaluation, val.trim());
}

function parseImplications(text: string, category: string): Implication[] {
    return text
        .split("\n")
        .map((line) => line.replace(/^\d+\.\s*/, "").trim())
        .filter((line) => line.length > 0)
        .map((implication) => ({ category, implication }));
}

function parseNextActions(text: string): NextAction[] {
    // Blocks of lines separated by blank line(s)
    // First line is action, the rest of the lines is the justification.
    const blocks = text.split(/\n\s*\n/);
    return blocks
        .filter((block) => block.trim().length > 0)
        .map((block) => {
            const lines = block.split("\n");
            return {
                action: lines[0].trim(),
                justification: lines
                    .slice(1)
                    .map((l) => l.trim())
                    .join("\n"),
            };
        });
}

function parseCSVDealAnalysisSummary(csv: string): ParsedSummaryResult {
    const { data: rows } = Papa.parse<string[]>(csv, { header: false });
    const errors: string[] = [];
    const summary: Partial<Summary> = {};

    // Row 0 is empty, row 1 is the header. Data rows start at 2.
    // Column 2 holds the output value.
    let i = 2;
    const next = () => rows[i++]?.[2]?.trim() || "";

    // What Happened This Round
    summary.text = next() || undefined;

    // Economic Gain Value: 1 dollar amount → realizedSavings; 2 amounts → estimatedSavings [low, high].
    try {
        const gainValue = next();
        if (gainValue) {
            const amounts = (gainValue.match(/\$[\d,]+(\.\d+)?/g) || []).map(parseNumber);
            if (amounts.length === 0) throw new Error(`No dollar amounts found`);
            const amountsInCents = amounts.map((a) => Math.round(a * 100));
            // Write both keys (one as undefined) so a re-import that switches modes
            // overrides the previous value through the shallow merge in handleSheetImport.
            if (amountsInCents.length === 1) {
                summary.realizedSavings = amountsInCents[0];
                summary.estimatedSavings = undefined;
            } else {
                summary.estimatedSavings = [amountsInCents[0], amountsInCents[1]];
                summary.realizedSavings = undefined;
            }
        }
    } catch (e: any) {
        errors.push(`Economic Gain Value: ${e.message}`);
    }

    // Strategic Progress: 1 number → prioritiesIdentified; 2 numbers → prioritiesAchieved + prioritiesIdentified.
    try {
        const progressValue = next();
        if (progressValue) {
            const numbers = progressValue.match(/\d+/g)?.map(Number);
            if (!numbers || numbers.length === 0) throw new Error(`No numbers found`);
            if (numbers.length === 1) {
                summary.strategicProgress = { prioritiesIdentified: numbers[0] };
            } else {
                summary.strategicProgress = {
                    prioritiesAchieved: numbers[0],
                    prioritiesIdentified: numbers[1],
                };
            }
        }
    } catch (e: any) {
        errors.push(`Strategic Progress: ${e.message}`);
    }

    // Implications
    const implications: Implication[] = [];
    for (const category of IMPLICATION_CATEGORIES) {
        const val = next();
        if (val) {
            implications.push(...parseImplications(val, category));
        }
    }
    summary.implications = implications;

    // Deal Evaluation
    const evaluation: Partial<DealEvaluation> = {};
    for (const field of [
        "overall",
        "pricing",
        "structuralFlexibility",
        "operationalAndLegalProtections",
        "strategicFit",
    ] as (keyof DealEvaluation)[]) {
        try {
            evaluation[field] = parseEvaluation(next());
        } catch (e: any) {
            errors.push(`Deal Evaluation (${field}): ${e.message}`);
        }
    }
    summary.evaluation = evaluation as DealEvaluation;

    // Next Actions
    try {
        const nextActions = parseNextActions(next());
        if (nextActions.length === 0) throw new Error("No actions found");
        summary.nextActions = nextActions;
    } catch (e: any) {
        errors.push(`Next Actions: ${e.message}`);
    }

    return { summary, errors };
}

// --- Component ---

interface SummaryTabProps {
    output: DealAnalysisOutput;
    onChange: (output: DealAnalysisOutput) => void;
}

const evaluationColor = (evaluation?: Evaluation): string => {
    switch (evaluation) {
        case Evaluation.FAVORABLE:
            return "green";
        case Evaluation.NOT_FAVORABLE:
            return "red";
        case Evaluation.NEUTRAL:
            return "blue";
        default:
            return "default";
    }
};

const evaluationLabel = (evaluation?: Evaluation): string => {
    switch (evaluation) {
        case Evaluation.FAVORABLE:
            return "Favorable";
        case Evaluation.NOT_FAVORABLE:
            return "Unfavorable";
        case Evaluation.NEUTRAL:
            return "Neutral";
        default:
            return "N/A";
    }
};

const formatUsd = (n: number) => "$" + n.toLocaleString("en-US");

const EvalTag: FC<{ evaluation?: Evaluation }> = ({ evaluation }) => (
    <Tag color={evaluationColor(evaluation)}>{evaluationLabel(evaluation)}</Tag>
);

/**
 * Import summary from Google sheet and saves it on DealAnalysisOutput.
 */
export const SummaryTab: FC<SummaryTabProps> = ({ output, onChange }) => {
    const summary = output.summary;
    const [googleSheetUrl, setGoogleSheetUrl] = useState("");
    const [isImportingSheet, setIsImportingSheet] = useState(false);
    const [importErrors, setImportErrors] = useState<string[]>([]);

    useEffect(() => {
        setGoogleSheetUrl(output.sourceUrls?.summary ?? "");
    }, [output]);

    const handleSheetImport = async () => {
        if (!googleSheetUrl.trim()) {
            message.warning("Please enter a Google Sheet URL or File ID");
            return;
        }
        setIsImportingSheet(true);
        setImportErrors([]);
        try {
            const response = await convertGoogleSheet(googleSheetUrl.trim());
            const csv = (response as any).data?.csv || (response as any).csv;
            if (csv) {
                const result = parseCSVDealAnalysisSummary(csv);
                setImportErrors(result.errors);
                onChange({
                    ...output,
                    summary: { ...summary, ...result.summary } as Summary,
                    sourceUrls: { ...output.sourceUrls, summary: googleSheetUrl.trim() },
                });
                if (result.errors.length === 0) {
                    message.success("Google Sheet imported into summary");
                }
            } else {
                message.warning("No CSV data found in Google Sheet response");
            }
        } catch (error) {
            handleError(error);
        } finally {
            setIsImportingSheet(false);
        }
    };
   
    return (
        <div>
            {/* Google Sheet import */}
            <Row gutter={8} style={{ marginBottom: 16 }}>
                <Col flex="auto">
                    <Input
                        placeholder="Enter Google Sheet URL or File ID"
                        value={googleSheetUrl}
                        onChange={(e) => setGoogleSheetUrl(e.target.value)}
                        onPressEnter={handleSheetImport}
                        disabled={isImportingSheet}
                    />
                </Col>
                <Col>
                    <Button
                        icon={<ImportOutlined />}
                        onClick={handleSheetImport}
                        loading={isImportingSheet}
                    >
                        Import
                    </Button>
                </Col>
            </Row>
            <GoogleShareHint />

            {/* Import Errors */}
            {importErrors.length > 0 && (
                <Alert
                    type="error"
                    style={{ marginBottom: 16 }}
                    message="Import errors — please fix the sheet and re-import"
                    description={
                        <ul style={{ margin: 0, paddingLeft: 20 }}>
                            {importErrors.map((err, i) => (
                                <li key={i}>{err}</li>
                            ))}
                        </ul>
                    }
                    closable
                    onClose={() => setImportErrors([])}
                />
            )}

            {summary && (
                <Descriptions bordered size="small" column={1}>
                    <Descriptions.Item label="What happened this round">
                        {summary.text || "—"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Economic gain">
                        {summary.estimatedSavings
                            ? `${formatUsd(summary.estimatedSavings[0] / 100)} – ${formatUsd(summary.estimatedSavings[1] / 100)}`
                            : summary.realizedSavings != null
                              ? formatUsd(summary.realizedSavings / 100)
                              : "—"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Strategic progress">
                        {summary.strategicProgress
                            ? summary.strategicProgress.prioritiesAchieved != null
                                ? `${summary.strategicProgress.prioritiesAchieved} of ${summary.strategicProgress.prioritiesIdentified} priorities achieved`
                                : `${summary.strategicProgress.prioritiesIdentified} priorities identified`
                            : "—"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Implications">
                        {summary.implications?.length > 0 ? (
                            <ul style={{ margin: 0, paddingLeft: 20 }}>
                                {summary.implications.map((imp, i) => (
                                    <li key={i}>
                                        <Tag>{imp.category}</Tag> {imp.implication}
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            "—"
                        )}
                    </Descriptions.Item>
                    <Descriptions.Item label="Deal evaluation">
                        {summary.evaluation ? (
                            <>
                                <strong>Overall:</strong>{" "}
                                <EvalTag evaluation={summary.evaluation.overall} />
                                <br />
                                Pricing: <EvalTag evaluation={summary.evaluation.pricing} />
                                <br />
                                Structural flexibility:{" "}
                                <EvalTag evaluation={summary.evaluation.structuralFlexibility} />
                                <br />
                                Operational / legal:{" "}
                                <EvalTag
                                    evaluation={summary.evaluation.operationalAndLegalProtections}
                                />
                                <br />
                                Strategic fit:{" "}
                                <EvalTag evaluation={summary.evaluation.strategicFit} />
                            </>
                        ) : (
                            "—"
                        )}
                    </Descriptions.Item>
                    <Descriptions.Item label="Next actions">
                        {summary.nextActions?.length > 0 ? (
                            <ul style={{ margin: 0, paddingLeft: 20 }}>
                                {summary.nextActions.map((a, i) => (
                                    <li key={i}>
                                        <strong>{a.action}</strong>
                                        {a.justification && <> — {a.justification}</>}
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            "—"
                        )}
                    </Descriptions.Item>
                </Descriptions>
            )}
        </div>
    );
};
